import { useMutation, useQueryClient } from "@tanstack/react-query";
import api from "../../../api";
import { useOnSiteStructureChanged } from "../../../hooks/useOnSiteStructureChanged";
import {
  mergePersonIntoListCache,
  personQueries
} from "../../../hooks/usePerson";
import urls from "../../../urls";
import type { Person } from "../../../utils/backend-types";

interface PersonMutationsProps {
  variantId: number;
}

export function usePersonMutations({ variantId }: PersonMutationsProps) {
  const queryClient = useQueryClient();
  const onSiteStructureChanged = useOnSiteStructureChanged();

  const listQueryKey = personQueries.list({ variantId }).queryKey;

  const invalidateAllPersons = () =>
    queryClient.invalidateQueries({
      predicate: (query) => query.queryKey[0] === "persons"
    });

  const createPersonMutation = useMutation({
    mutationFn: ({
      newData,
      graphCoordinates
    }: {
      newData: Person;
      graphCoordinates: { x: number; y: number };
    }) => createPerson(newData, graphCoordinates, variantId),
    onSuccess: (response) => {
      mergePersonIntoListCache(queryClient, { variantId }, response.data);

      invalidateAllPersons();
      onSiteStructureChanged();
    }
  });

  const editPersonMutation = useMutation({
    mutationFn: (editedData: Person) => editPerson(editedData),
    onSuccess: (response) => {
      const detailQueryKey = personQueries.detail(response.data.id).queryKey;

      queryClient.setQueryData(listQueryKey, (old: Array<Person> | undefined) =>
        old?.map((p) => (p.id === response.data.id ? response.data : p))
      );
      queryClient.setQueryData(detailQueryKey, response.data);

      invalidateAllPersons();

      onSiteStructureChanged();
    }
  });

  const deletePersonMutation = useMutation({
    mutationFn: (personId: number) => deletePerson(personId),
    onSuccess() {
      invalidateAllPersons();
      onSiteStructureChanged();
    }
  });

  return {
    createPersonMutation,
    editPersonMutation,
    deletePersonMutation
  };
}

function createPerson(
  newPerson: Person,
  graphCoordinates: { x: number; y: number },
  variantId: number
) {
  const url = urls.api.personsByVariantId(variantId);

  return api.post<Person>(url, {
    ...newPerson,
    sketchElement: {
      xPosition: graphCoordinates.x,
      yPosition: graphCoordinates.y
    },
    variant: variantId
  });
}

function editPerson(editedPerson: Person) {
  const url = urls.api.person(editedPerson.id);
  return api.patch<Person>(url, editedPerson);
}

function deletePerson(personId: number) {
  const url = urls.api.person(personId);
  return api.delete(url);
}
